import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Divider, Text
} from 'components/ui';
import DefaultPaymentCheckbox from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo/DefaultPaymentCheckbox';
import {
    colors, fontSizes, fontWeights, mediaQueries, space
} from 'style/config';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';

const VenmoExpandedView = ({
    venmoHeader = 'Pay with Venmo',
    venmoSaveAndContinue = 'Save and Continue',
    venmoCancel = 'Cancel',
    onSaveAndContinue,
    onCancel,
    isAnonymous
}) => {
    const headerRef = useRef(null);

    useEffect(() => {
        headerRef.current?.focus();

        FrictionlessCheckoutBindings.setAlternatePaymentExpandedViewAnalytics('venmo');
    }, []);

    return (
        <Box
            role='region'
            aria-labelledby='venmo-expanded-header'
            data-testid='venmo-expanded-view'
        >
            <Text
                as='h3'
                id='venmo-expanded-header'
                ref={headerRef}
                tabIndex={-1}
                css={styles.header}
            >
                {venmoHeader}
            </Text>
            {!isAnonymous && (
                <Box css={styles.checkboxContainer}>
                    <DefaultPaymentCheckbox
                        paymentName='venmo'
                        useSelectedDefaultOnly
                    />
                </Box>
            )}
            <Divider css={styles.divider} />
            <Box css={styles.buttonContainer}>
                <Button
                    variant='secondary'
                    css={styles.cancelButton}
                    onClick={onCancel}
                    data-testid='venmo-cancel'
                    aria-label='Cancel Venmo selection and return'
                >
                    {venmoCancel}
                </Button>
                <Button
                    variant='primary'
                    css={styles.saveButton}
                    onClick={onSaveAndContinue}
                    data-testid='venmo-save-continue'
                    aria-label='Save Venmo and return to payment options'
                >
                    {venmoSaveAndContinue}
                </Button>
            </Box>
        </Box>
    );
};

const styles = {
    header: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: '18px',
        marginTop: space[3],
        marginBottom: space[1]
    },
    checkboxContainer: {
        marginTop: space[2],
        marginBottom: space[2]
    },
    divider: {
        marginTop: space[3],
        marginBottom: space[3],
        borderColor: colors.lightGray,
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block'
        }
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: space[2],
        [mediaQueries.sm]: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            gap: space[3]
        }
    },
    cancelButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    },
    saveButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    }
};

VenmoExpandedView.propTypes = {
    venmoHeader: PropTypes.string,
    venmoSaveAndContinue: PropTypes.string,
    venmoCancel: PropTypes.string,
    onSaveAndContinue: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isAnonymous: PropTypes.bool
};

export default wrapFunctionalComponent(VenmoExpandedView, 'VenmoExpandedView');
